import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import MuiTable from "../../../components/Tables/MuiTable";

function FlexibilityDatatable({ data }) {
  const [rows, setRows] = useState([]);

  const { t } = useTranslation(["flexibility"]);

  useEffect(() => {
    if (data && data.length > 0) {
      const sortedData = [...data].sort(
        (a, b) => new Date(b.createdDate || 0) - new Date(a.createdDate || 0)
      );

      setRows(
        sortedData.map((flex) => ({
          id: flex.flexibility_offer_id,
          minQuantity: flex.min_quantity,
          maxQuantity: flex.max_quantity,
          startDatetime: new Date(flex.start_datetime).toLocaleString(),
          endDatetime: new Date(flex.end_datetime).toLocaleString(),
          status: flex.status,
          maxNumberActivations: flex.max_number_activations,
          activationPrice: flex.activation_price,
          divisible: flex.divisible ? (
            <CheckCircleIcon color="success" />
          ) : (
            <CancelIcon color="error" />
          ),
        }))
      );
    } else {
      setRows([]);
    }
  }, [data]);

  const smallCellStyle = {
    fontSize: "0.75rem",
    wordBreak: "break-word",
    maxWidth: "200px",
    whiteSpace: "normal",
  };

  const columns = [
    {
      label: t("table.columns.id"),
      name: "id",
      options: {
        sortThirdClickReset: true,
        customBodyRenderLite: (dataIndex) => (
          <span style={smallCellStyle}>{rows[dataIndex]?.id}</span>
        ),
      },
    },
    { label: t("table.columns.min_qty"), name: "minQuantity" },
    { label: t("table.columns.max_qty"), name: "maxQuantity" },
    {
      label: t("table.columns.start"),
      name: "startDatetime",
      options: {
        customBodyRenderLite: (dataIndex) => (
          <span style={smallCellStyle}>{rows[dataIndex]?.startDatetime}</span>
        ),
      },
    },
    {
      label: t("table.columns.end"),
      name: "endDatetime",
      options: {
        customBodyRenderLite: (dataIndex) => (
          <span style={smallCellStyle}>{rows[dataIndex]?.endDatetime}</span>
        ),
      },
    },
    {
      label: t("table.columns.status"),
      name: "status",
      options: {
        filter: true,
        searchable: false,
        viewColumns: false,
      },
    },
    {
      label: t("table.columns.max_activations"),
      name: "maxNumberActivations",
      options: { sortThirdClickReset: true },
    },
    { label: t("table.columns.activation_price"), name: "activationPrice" },
    {
      label: t("table.columns.divisible"),
      name: "divisible",
      options: {
        filter: false,
        sort: false,
        searchable: false,
        viewColumns: true,
      },
    },
  ];

  return (
    <MuiTable
      table={{
        columns,
        rows,
      }}
      canSearch
    />
  );
}

FlexibilityDatatable.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      flexibility_offer_id: PropTypes.string.isRequired,
      min_quantity: PropTypes.number.isRequired,
      max_quantity: PropTypes.number.isRequired,
      start_datetime: PropTypes.string.isRequired,
      end_datetime: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      max_number_activations: PropTypes.number.isRequired,
      activation_price: PropTypes.number.isRequired,
      divisible: PropTypes.bool.isRequired,
      createdDate: PropTypes.string, // opcional
    })
  ),
};

FlexibilityDatatable.defaultProps = {
  data: [],
};

export default FlexibilityDatatable;
